import { memo } from 'react';
import { useComputed } from '@preact/signals';
import { Button, type ButtonProps } from '@nespresso-design-system/components';
import ModalOnDemand from '@/widgets/components/controls/ModalOnDemand/ModalOnDemand';
import useMediaQuery from '@/widgets/hooks/useMediaQuery.tsx';
import useQueryStringStore from '@/widgets/hooks/useQueryStringStore';
import useTranslations from '@/widgets/hooks/useTranslations';
import type { Translations } from '@/widgets/ProductList/config';
import type { PersistedState } from '@/widgets/ProductList/Store';
import type { FiltersAndSort } from '@/widgets/services/FilteringAndSortingService';
import useTitleObserver from '../../hooks/useTitleObserver.ts';
import { trackFilterAllOpened } from '../../Tracking.ts';
import styles from './AllFiltersDrawer.module.scss';
import { SortingList } from './parts/SortingList.tsx';
import ClearButton from './parts/ClearButton.tsx';
import ProductResultsButton from './parts/ProductResultsButton.tsx';
import QuickFilterList from './parts/QuickFilterList.tsx';
import FilterList from './parts/FilterList.tsx';

type AllFiltersButtonProps = {
  filtersAndSort: FiltersAndSort;
};

const MODAL_NAME: Required<PersistedState>['openModal'] = 'all-filters';

function AllFiltersDrawer({ filtersAndSort }: AllFiltersButtonProps) {
  const [{ openModal }, setPersistedState] =
    useQueryStringStore<PersistedState>();

  const { filtering, products, sorting } = filtersAndSort;
  const { t } = useTranslations<Translations>();
  const { isMobile } = useMediaQuery();
  const close = () => setPersistedState({ openModal: undefined });
  const isOpen = useComputed(() => openModal.value === MODAL_NAME);

  const { title, ref } = useTitleObserver({
    onVisible: () =>
      isMobile
        ? t('productList.sort.label')
        : t('productList.filtersDrawer.title'),
    onInvisible: () => t('productList.filtersDrawer.title')
  });

  return (
    <ModalOnDemand
      closeBtnA11yLabel={title}
      isOpen={isOpen.value}
      position="side"
      size="medium"
      fixedFooter
      footerSlot={
        <div class={styles.buttons}>
          <ClearButton filtersAndSort={filtersAndSort} />
          <ProductResultsButton products={products} onClick={close} />
        </div>
      }
      heading={title}
      onClose={close}
    >
      {isMobile && (
        <>
          <SortingList sorting={sorting} />
          <hr class={styles.separator} />
          <h2 class={styles.subtitle} ref={ref}>
            {t('productList.filtersDrawer.title')}
          </h2>
        </>
      )}
      <QuickFilterList filtering={filtering} />
      <FilterList filtering={filtering} />
    </ModalOnDemand>
  );
}

export default memo(AllFiltersDrawer);

export function AllFiltersButton() {
  const { t } = useTranslations<Translations>();
  const [, setPersistedState] = useQueryStringStore<PersistedState>();
  const label = t('productList.filtersDrawer.buttonLabel');
  const labelMobile = t('productList.filtersDrawer.buttonLabelMobile');
  const { isMobile } = useMediaQuery();

  const open = () => {
    trackFilterAllOpened();
    setPersistedState({ openModal: MODAL_NAME });
  };

  return (
    <Button
      aria-label={label}
      label={isMobile ? labelMobile : label}
      onClick={open}
      icon="24/symbol/filter"
      // Note: there is a type mismatch here between DS & Preact
      size={'small' as ButtonProps['size']}
      variant="ghost"
      iconPosition="leading"
    />
  );
}
